import {useDispatch} from "react-redux";
import React from "react";
import {format} from "date-fns";
import YesNoLabel from "../../../../components/YesNoLabel";
import {useLicensesUIContext} from "../../LicensesUIContext";
import * as companyApiKeysActions from "../store/companyApiKeysActions";
import {useCompanyApiKeysStateSelector} from "./useCompanyApiKeysSelectors";
import ActionsColumnFormatter from "../components/ActionsColumnFormatter";

export const COMPANY_API_KEYS_TAB_KEY = "company-api-keys";

function dateFormatter(value) {
  const dateValue = value ? format(new Date(value), 'dd-MM-yyyy HH:mm') : '';
  return <span>{dateValue}</span>
}

function yesNoFormatter(value) {
  return <YesNoLabel value={value}/>
}

function useCompanyApiKeysTableCols(onRevoke, onReactivate) {
  return React.useMemo(() => {
    return [
      {
        text: "Reseller Name",
        dataField: "resellerName",
      },
      {
        text: "Company Name",
        dataField: "companyName",
      },
      {
        text: "API Key",
        dataField: "apiKey",
      },
      {
        text: "Active",
        dataField: "isActive",
        formatter: yesNoFormatter,
      },
      {
        text: "Revoked",
        dataField: "isRevoked",
        formatter: yesNoFormatter,
      },
      {
        text: "Default Migrated",
        dataField: "isDefaultMigrated",
        formatter: yesNoFormatter,
      },
      {
        text: "Expires At",
        dataField: "expiresAt",
        formatter: dateFormatter,
      },
      {
        text: "Last Used At",
        dataField: "lastUsedAt",
        formatter: dateFormatter,
      },
      {
        text: "Created At",
        dataField: "createdAt",
        formatter: dateFormatter,
      },
      {
        text: "Revoked At",
        dataField: "revokedAt",
        formatter: dateFormatter,
      },
      {
        text: "Action",
        dataField: "Actions",
        formatter: (cellContent, row) => (
          <ActionsColumnFormatter row={row} onRevoke={onRevoke} onReactivate={onReactivate}/>
        ),
        classes: "text-right pr-0",
        headerClasses: "text-right pr-3",
        style: {
          minWidth: "100px",
        },
      },
    ];
  }, [onRevoke, onReactivate])
}

export function useCompanyApiKeys(queryParams, optionsResellerId, onRevoke, onReactivate) {
  const dispatch = useDispatch();
  const licensesUIContext = useLicensesUIContext();
  const companyApiKeysState = useCompanyApiKeysStateSelector();
  const tableCols = useCompanyApiKeysTableCols(onRevoke, onReactivate);
  const isActiveTab = COMPANY_API_KEYS_TAB_KEY === licensesUIContext.activeTabKey;

  React.useEffect(() => {
    if (isActiveTab) {
      dispatch(companyApiKeysActions.fetchCompanyApiKeyFilterOptions(optionsResellerId))
    }
  }, [dispatch, isActiveTab, optionsResellerId])

  React.useEffect(() => {
    if (isActiveTab) {
      dispatch(companyApiKeysActions.fetchCompanyApiKeys(queryParams))
    }
    return () => dispatch(companyApiKeysActions.resetCompanyApiKeysState())
  }, [dispatch, isActiveTab, queryParams])

  const reload = React.useCallback(() => {
    return dispatch(companyApiKeysActions.fetchCompanyApiKeys(queryParams))
  }, [dispatch, queryParams])

  return {
    reload,
    totalCount: companyApiKeysState.totalCount,
    entities: companyApiKeysState.companyApiKeyItems,
    companyOptions: companyApiKeysState.filterOptions.companies,
    resellerOptions: companyApiKeysState.filterOptions.resellers,
    loading: companyApiKeysState.listLoading,
    tableCols
  }
}
